import { TEMPLATE_BROADCAST, BROADCAST_HIDDEN_ROUTES, BROADCAST_ONLY_ROUTES } from '@/components/Sites/siteTemplates'

/**
 * Grouped navigation of one site's management pages. Route *names* are the
 * stable API (deep links and `:to="{ name }"` all over the Sites code); the
 * router maps them to grouped paths and redirects the old flat ones.
 *
 * Entries may carry `templates` (which home-page templates show them) and
 * `when` (a predicate over the nav context); `buildSiteNav` filters on both.
 * `lockedWhen` keeps an entry but marks it `locked` (plan limit: lock icon).
 * The broadcast template (sites-broadcast-template-contract.md §7) hides
 * Categories and Featured, adds Settings › Broadcast and, with native
 * giving, Monetize › Donations, plus Settings › Audience (preset and
 * register-to-watch, v0.19).
 */
export const SITE_NAV = [
	{ id: 'overview', label: 'Overview', route: 'SiteOverview' },
	{
		id: 'content',
		label: 'Content',
		children: [
			{ label: 'Library', route: 'SiteContent' },
			{ label: 'Categories', route: 'SiteCategories', templates: ['catalog'] },
			{ label: 'Featured', route: 'SiteFeatured', templates: ['catalog'] },
			{ label: 'Pages', route: 'SitePages' },
			{ label: 'Calendar', route: 'SiteCalendar' },
		],
	},
	{
		id: 'monetize',
		label: 'Monetize',
		children: [
			{ label: 'Plans', route: 'SitePlans' },
			{ label: 'Promo codes', route: 'SitePromos' },
			{ label: 'Transactions', route: 'SiteTransactions' },
			{ label: 'Donations', route: 'SiteDonations', templates: [TEMPLATE_BROADCAST], when: (ctx) => ctx.givingMode === 'native' },
		],
	},
	{
		id: 'audience',
		label: 'Audience',
		children: [
			{ label: 'Viewers', route: 'SiteViewers' },
		],
	},
	{
		id: 'settings',
		label: 'Settings',
		children: [
			{ label: 'Broadcast', route: 'SiteBroadcast', templates: [TEMPLATE_BROADCAST] },
			{ label: 'Audience', route: 'SiteAudience', templates: [TEMPLATE_BROADCAST] },
			{ label: 'Theme', route: 'SiteTheme' },
			{ label: 'Domains', route: 'SiteDomains', lockedWhen: (ctx) => ctx.customDomainLocked },
			{ label: 'General', route: 'SiteGeneral' },
		],
	},
]

const entryShown = (entry, ctx) => {
	if (Array.isArray(entry.templates) && !entry.templates.includes(ctx.template)) return false
	if (typeof entry.when === 'function' && !entry.when(ctx)) return false
	return true
}

/**
 * The navigation for one site.
 * @param {{ template?: 'catalog'|'broadcast', givingMode?: 'off'|'native', customDomainLocked?: boolean }} [ctx]
 */
export function buildSiteNav(ctx = {}) {
	const context = {
		template: ctx.template === TEMPLATE_BROADCAST ? TEMPLATE_BROADCAST : 'catalog',
		givingMode: ctx.givingMode || 'off',
		customDomainLocked: Boolean(ctx.customDomainLocked),
	}
	const withLock = (entry) => (typeof entry.lockedWhen === 'function' ? { ...entry, locked: Boolean(entry.lockedWhen(context)) } : entry)
	return SITE_NAV
		.filter((group) => entryShown(group, context))
		.map((group) => (group.children ? { ...group, children: group.children.filter((child) => entryShown(child, context)).map(withLock) } : withLock(group)))
		.filter((group) => !group.children || group.children.length > 0)
}

/** The group (and child, when any) owning a route name; looks through every entry, shown or not. */
export function findNavEntry(routeName, nav = SITE_NAV) {
	for (const group of nav) {
		if (group.route === routeName) return { group, child: null }
		const child = (group.children || []).find((c) => c.route === routeName)
		if (child) return { group, child }
	}
	return { group: null, child: null }
}

/**
 * Why a route is not part of the template's navigation, or null when it is.
 * Deep links to such pages still resolve; the shell shows a one-line notice.
 * @return {'hidden'|'broadcast-only'|null}
 */
export function routeTemplateMismatch(routeName, template) {
	if (template === TEMPLATE_BROADCAST) return BROADCAST_HIDDEN_ROUTES.includes(routeName) ? 'hidden' : null
	return BROADCAST_ONLY_ROUTES.includes(routeName) ? 'broadcast-only' : null
}
